// export-import.js — Export, backup, and import operations.
//
// Handles exporting notes as HTML (single or notebook), backing up as ZIP,
// and importing from ZIP or markdown files. All platforms trigger a browser
// download for exports and backups.

import {
  CALENDARS_NOTE,
  GRAPH_NOTE,
  getCurrentFileName,
  importZipInput,
} from '../core/app-state.js';
import { NoteEditor } from '../editor/note-editor.js';
import { confirmStatus, updateStatus } from '../ui/status-pill.js';
import { downloadBlob } from '../utils/download.js';
import {
  noteNameToAttachmentDir,
  sanitizeAttachmentName,
  uniqueAttachmentName,
} from './attachment-helpers.js';
import { getVisibleNotes, updateFileList } from './file-list.js';
import { getNoteTitle } from './note-manager.js';

import {
  _noteNameFromContent,
  generateHtmlContent,
  generateNotebookHtml,
  getExportThemeCSSRoot,
  preEvaluateOpenEquations,
  processNoteForExport,
  stripBakedOpenEquations,
} from './export-html.js';

// HTML rendering layer → extracted to export-html.ts (imported above).
// ── Download helper ────────────────────────────────────────────────────────
// On iOS (WKWebView) the `download` attribute on anchor tags is not honoured
// and blob URL clicks are silently ignored. Use navigator.share() instead,
// which opens the native iOS share sheet so the user can save to Files,
// send via Mail, etc. Falls back to the standard link.click() on all other
// platforms where it works reliably.

/**
 * @param {any} blob
 * @param {string} filename
 * @returns {Promise<void>}
 */
async function triggerDownload(blob: any, filename: any) {
  if (window.Platform?.isCapacitor) {
    const file = new File([blob], filename, { type: blob.type });
    const shared = await window.Platform.attachments.share({ file, filename });
    if (shared) return;
    // Fallback: shouldn't normally be reached on iOS 15+
    updateStatus('Share not supported on this device.', false);
    return;
  }
  downloadBlob(blob, filename);
}

// ── Export operations ──────────────────────────────────────────────────────

/** @returns {Promise<void>} */
async function exportNote() {
  const name = getCurrentFileName() || getNoteTitle();
  if (!name) {
    updateStatus('No note selected.', { kind: 'warning' });
    return;
  }
  updateStatus(`Exporting\u2026`, true, true);
  const markdown = NoteEditor.getContent();
  const html = await generateHtmlContent(name, markdown, name);
  const blob = new Blob([html], { type: 'text/html' });
  await triggerDownload(blob, `${name}.html`);
  updateStatus(`Exported "${name}".`, true);
}

/** @returns {Promise<void>} */
async function exportAllNotes() {
  const entries = await window.NoteStorage.getAllNotes();
  if (entries.length === 0) {
    updateStatus('No notes to export yet.', { kind: 'info' });
    return;
  }
  updateStatus(`Exporting\u2026`, true, true);
  entries.sort((a: any, b: any) => b.name.localeCompare(a.name));
  const html = await generateNotebookHtml(entries);
  const blob = new Blob([html], { type: 'text/html' });
  await triggerDownload(blob, 'notes_notebook.html');
  updateStatus(`Exported ${entries.length} note${entries.length === 1 ? '' : 's'}.`, true);
}

/**
 * Materialise a set of note names into `{ name, content }` entries, dropping any
 * that no longer resolve (deleted between listing and reading).
 * @param {string[]} notes
 * @returns {Promise<{ name: string; content: string }[]>}
 */
async function buildNoteEntries(notes: string[]) {
  const results = await Promise.all(
    notes.map(async (name) => {
      const content = await window.NoteStorage.getNote(name);
      return content !== null ? { name, content } : null;
    })
  );
  return results.filter((e): e is { name: string; content: string } => e !== null);
}

/** @returns {Promise<void>} */
async function exportSelectedNotes() {
  const notes = await getVisibleNotes();
  if (notes.length === 0) {
    updateStatus('No notes match the filter.', { kind: 'info' });
    return;
  }
  updateStatus(`Exporting\u2026`, true, true);
  const entries = await buildNoteEntries(notes);
  const html = await generateNotebookHtml(entries);
  const blob = new Blob([html], { type: 'text/html' });
  await triggerDownload(blob, 'notes_notebook.html');
  updateStatus(`Exported ${entries.length} note${entries.length === 1 ? '' : 's'}.`, true);
}

// ── Backup operations ─────────────────────────────────────────────────────

/**
 * @param {any[]} noteEntries - [{ name, content }, …].
 * @param {string} downloadName
 * @returns {Promise<void>}
 */
async function createBackupZip(noteEntries: any, downloadName: any) {
  const zip = new window.JSZip();
  const total = noteEntries.length;
  updateStatus(`Backing up (0/${total})\u2026`, true, true);
  let completed = 0;
  await Promise.all(
    noteEntries.map(async ({ name, content }: any) => {
      // Bake both inline open-equation results AND computed-column values into the
      // backed-up markdown, in the SAME pass + order as the HTML export
      // (generateHtmlContent) — otherwise, now that inline math is column-aware, a
      // .md backup would bake inline column-reads differently from an HTML export
      // (the former asymmetry: the backup ran only materializeTableColumns). Both
      // keep their formula headers, so the .md round-trips back into a live note.
      zip.file(`${name}.md`, processNoteForExport(content));
      const attFiles = await window.NoteStorage.listAttachments(name);
      const attDir = noteNameToAttachmentDir(name);
      await Promise.all(
        attFiles.map(async (filename: any) => {
          const b64 = await window.NoteStorage.readAttachment(name, filename);
          if (b64) zip.file(`${attDir}/${filename}`, b64, { base64: true });
        })
      );
      completed++;
      updateStatus(`Backing up (${completed}/${total})\u2026`, true, true);
    })
  );
  updateStatus(`Compressing\u2026`, true, true);
  const blob = await zip.generateAsync({ type: 'blob' });
  await triggerDownload(blob, downloadName);
}

/** @returns {Promise<void>} */
async function downloadAllNotes() {
  updateStatus(`Backing up\u2026`, true, true);

  const allNotes = await window.NoteStorage.getAllNotes();
  if (allNotes.length === 0) {
    updateStatus('No notes to back up yet.', { kind: 'info' });
    return;
  }

  try {
    await createBackupZip(allNotes, 'all_notes.zip');
  } catch {
    updateStatus('Backup failed.', false);
    return;
  }

  updateStatus(`Backed up ${allNotes.length} note${allNotes.length === 1 ? '' : 's'}.`, true);
}

// ── Import ────────────────────────────────────────────────────────────────

/**
 * Guard an import against clobbering existing notes: check which of `entries`
 * already exist and, if any do, ask for confirmation. Returns `true` to proceed
 * (nothing exists, or the user confirmed) and `false` to abort (after setting
 * the cancelled status and clearing the file input).
 * @param {{ name: string }[]} entries
 * @returns {Promise<boolean>}
 */
async function confirmOverwrite(entries: { name: string }[]) {
  // A note's name is in getAllNoteNames() iff getNote(name) !== null (both key on
  // deleted=0, and an undecryptable note is absent from both), so a single
  // name-only set lookup answers "would this be overwritten?" without fetching —
  // and, on the encrypted path, AES-GCM-decrypting — every existing note's content.
  const existingSet = new Set(await window.NoteStorage.getAllNoteNames());
  const existingNames = entries.filter(({ name }) => existingSet.has(name)).map(({ name }) => name);
  if (existingNames.length === 0) return true;
  const list =
    existingNames.length <= 5
      ? existingNames.map((n) => `"${n}"`).join(', ')
      : `${existingNames
          .slice(0, 5)
          .map((n) => `"${n}"`)
          .join(', ')} and ${existingNames.length - 5} more`;
  const ok = await confirmStatus(
    `Overwrite ${existingNames.length} existing note${existingNames.length === 1 ? '' : 's'}? Importing replaces: ${list}.`,
    { kind: 'error', confirmLabel: 'Overwrite' }
  );
  if (!ok) {
    updateStatus('Import cancelled.', false);
    (importZipInput as any).value = '';
    return false;
  }
  return true;
}

/**
 * @param {any} files - a FileList/array of .md files.
 * @returns {Promise<void>}
 */
async function importNotesFromMd(files: any) {
  updateStatus(`Importing\u2026`, true, true);
  try {
    const entries = [];
    for (const file of files) {
      const fileBaseName = file.name.replace(/\.md$/, '');
      // Skip system/virtual notes — they are auto-generated and should not be imported
      if (fileBaseName === GRAPH_NOTE || fileBaseName === CALENDARS_NOTE) continue;
      const content = await file.text();
      // Use the first-line # header as the note name so that the name always
      // matches the header, falling back to the filename if no header is present.
      const name = _noteNameFromContent(fileBaseName, content);
      if (name === GRAPH_NOTE || name === CALENDARS_NOTE) continue;
      entries.push({ name, content });
    }

    // Check for existing notes that would be overwritten
    if (!(await confirmOverwrite(entries))) return;

    updateStatus(`Importing\u2026`, true, true);
    // Strip baked open-equation answers so `$expr = value$` computes LIVE again
    // (inverts the export/backup bake). Applied to the derived `content` after
    // the name was taken, so note names are unaffected.
    await Promise.all(
      entries.map(({ name, content }) =>
        window.NoteStorage.setNote(name, stripBakedOpenEquations(content))
      )
    );
    await updateFileList();
    (importZipInput as any).value = '';
    updateStatus(`Imported ${entries.length} note${entries.length === 1 ? '' : 's'}.`, true);
  } catch (err: any) {
    updateStatus(`Import failed: ${err.message}`, false);
  }
}

/**
 * @param {any} file - the .zip backup file.
 * @returns {Promise<void>}
 */
async function importNotesFromZip(file: any) {
  updateStatus(`Importing\u2026`, true, true);
  try {
    const zip = await window.JSZip.loadAsync(file);
    const rawEntries: any = [];
    const attachmentEntries: any = [];
    zip.forEach((relativePath: any, zipEntry: any) => {
      if (zipEntry.dir) return;
      if (relativePath.endsWith('.md')) {
        // Track the original dir name for attachment matching before we
        // derive the final note name from the header content below.
        rawEntries.push({ fileBaseName: relativePath.replace(/\.md$/, ''), zipEntry });
      } else if (relativePath.includes('.attachments/')) {
        attachmentEntries.push({ relativePath, zipEntry });
      }
    });

    // Read content upfront so we can derive note names from first-line headers.
    const entries = (
      await Promise.all(
        rawEntries.map(async ({ fileBaseName, zipEntry }: any) => {
          const content = await zipEntry.async('string');
          // Skip system/virtual notes — they are auto-generated and should not be imported
          if (fileBaseName === GRAPH_NOTE || fileBaseName === CALENDARS_NOTE) return null;
          // Use the first-line # header as the note name so that the name always
          // matches the header, falling back to the filename if no header is present.
          const name = _noteNameFromContent(fileBaseName, content);
          if (name === GRAPH_NOTE || name === CALENDARS_NOTE) return null;
          return { name, fileBaseName, content };
        })
      )
    ).filter(Boolean);

    // Check for existing notes that would be overwritten
    if (!(await confirmOverwrite(entries))) return;

    updateStatus(`Importing\u2026`, true, true);
    // Strip baked open-equation answers (inverts the export/backup bake) so a
    // re-imported note computes LIVE again.
    await Promise.all(
      entries.map(({ name, content }) =>
        window.NoteStorage.setNote(name, stripBakedOpenEquations(content))
      )
    );
    // Track names already taken per note so two zip entries that sanitize to
    // the same filename ("photo 1.jpg" + "photo_1.jpg") don't overwrite each
    // other \u2014 uniqueAttachmentName disambiguates with _2, _3, \u2026
    const takenByNote = new Map();
    const results = await Promise.allSettled(
      attachmentEntries.map(async ({ relativePath, zipEntry }: any) => {
        const slashIdx = relativePath.indexOf('/');
        if (slashIdx < 0) return { skipped: true };
        const dirPart = relativePath.slice(0, slashIdx);
        const rawFilename = relativePath.slice(slashIdx + 1);
        if (!rawFilename) return { skipped: true };
        // ZIP entry paths can include `..` segments and nested directories;
        // reduce to the bare basename, drop traversal markers, and run
        // through the same sanitizer export uses so an evil archive can't
        // land a file with a path-significant name.
        const baseName = rawFilename.split(/[/\\]/).pop();
        if (!baseName || baseName === '.' || baseName === '..') return { skipped: true };
        const sanitized = sanitizeAttachmentName(baseName);
        if (!sanitized) return { skipped: true };
        // Match attachments by the original file-based attachment dir name OR
        // by the header-derived note name's attachment dir.
        const matchedEntry = entries.find(
          (e) =>
            noteNameToAttachmentDir(e.fileBaseName) === dirPart ||
            noteNameToAttachmentDir(e.name) === dirPart
        );
        if (!matchedEntry) return { skipped: true };
        let taken = takenByNote.get(matchedEntry.name);
        if (!taken) {
          taken = new Set();
          takenByNote.set(matchedEntry.name, taken);
        }
        const filename = uniqueAttachmentName(taken, sanitized);
        taken.add(filename);
        const b64 = await zipEntry.async('base64');
        await window.NoteStorage.writeAttachment(matchedEntry.name, filename, b64);
        return { written: true };
      })
    );
    let failed = 0;
    for (const r of results) {
      if (r.status === 'rejected') {
        failed++;
        console.error('[import] attachment failed:', r.reason);
      } else if (r.value && (r.value as any).written === false) {
        failed++;
      }
    }
    await updateFileList();
    (importZipInput as any).value = '';
    const noteWord = entries.length === 1 ? '' : 's';
    updateStatus(
      failed > 0
        ? `Imported ${entries.length} note${noteWord} (${failed} attachment${failed === 1 ? '' : 's'} skipped).`
        : `Imported ${entries.length} note${noteWord}.`,
      failed === 0
    );
  } catch (err: any) {
    updateStatus(`Import failed: ${err.message}`, false);
  }
}

// The window.* back-compat mirror for the export/import surface had no readers
// left (app-init imports the ESM names; the test captures the module export)
// and is gone.

export {
  createBackupZip,
  downloadAllNotes,
  exportAllNotes,
  exportNote,
  exportSelectedNotes,
  generateHtmlContent,
  generateNotebookHtml,
  getExportThemeCSSRoot,
  importNotesFromMd,
  importNotesFromZip,
  preEvaluateOpenEquations,
};
